import { useTranslation } from 'react-i18next';
import type { OnboardingData } from './types.js';

interface Props {
  data: OnboardingData;
  onChange: (patch: Partial<OnboardingData>) => void;
  onBack: () => void;
  onNext: () => void;
}

const BUCKET_SUGGESTIONS = ['Projects', 'Jobs', 'Opportunities', 'Campaigns', 'Contracts'];
const ORDER_SUGGESTIONS = ['Orders', 'Tasks', 'Builds', 'Runs', 'Requests'];

export default function StepLabels({ data, onChange, onBack, onNext }: Props) {
  const { t } = useTranslation('onboarding');

  function setBucketLabel(value: string) {
    onChange({
      bucketLabel: value.slice(0, -1) || value,
      bucketLabelPlural: value,
    });
  }

  function setOrderLabel(value: string) {
    onChange({
      orderLabel: value.slice(0, -1) || value,
      orderLabelPlural: value,
    });
  }

  return (
    <div className="page-enter">
      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--weight-light)',
          color: 'var(--color-text)',
          marginBottom: 'var(--space-8)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        {t('labels.title')}
      </h2>

      {/* Bucket label */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <p
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--color-text)',
            marginBottom: 'var(--space-3)',
          }}
        >
          {t('labels.bucket_label')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          {BUCKET_SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setBucketLabel(s)}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                border: data.bucketLabelPlural === s
                  ? '1.5px solid var(--color-brand)'
                  : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-sm)',
                color: data.bucketLabelPlural === s ? 'var(--color-brand)' : 'var(--color-text)',
                background: data.bucketLabelPlural === s ? 'var(--color-brand-pale)' : 'var(--color-bg)',
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Singular
            </label>
            <input
              type="text"
              value={data.bucketLabel}
              onChange={(e) => onChange({ bucketLabel: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              {t('labels.bucket_plural_label')}
            </label>
            <input
              type="text"
              value={data.bucketLabelPlural}
              onChange={(e) => onChange({ bucketLabelPlural: e.target.value })}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Order label */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <p
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--color-text)',
            marginBottom: 'var(--space-3)',
          }}
        >
          {t('labels.order_label')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          {ORDER_SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setOrderLabel(s)}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                border: data.orderLabelPlural === s
                  ? '1.5px solid var(--color-brand)'
                  : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-sm)',
                color: data.orderLabelPlural === s ? 'var(--color-brand)' : 'var(--color-text)',
                background: data.orderLabelPlural === s ? 'var(--color-brand-pale)' : 'var(--color-bg)',
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              Singular
            </label>
            <input
              type="text"
              value={data.orderLabel}
              onChange={(e) => onChange({ orderLabel: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
              {t('labels.order_plural_label')}
            </label>
            <input
              type="text"
              value={data.orderLabelPlural}
              onChange={(e) => onChange({ orderLabelPlural: e.target.value })}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <button onClick={onBack} style={backBtnStyle}>
          ← {t('actions.back', { ns: 'common' })}
        </button>
        <button onClick={onNext} style={nextBtnStyle}>
          {t('actions.next', { ns: 'common' })} →
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-3) var(--space-4)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text)',
  background: 'var(--color-bg)',
  boxSizing: 'border-box',
};

const backBtnStyle: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-5)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-bg)',
  cursor: 'pointer',
};

const nextBtnStyle: React.CSSProperties = {
  flex: 1,
  padding: 'var(--space-3) var(--space-5)',
  background: 'var(--color-brand)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)',
  cursor: 'pointer',
};
